import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Icon } from '@iconify/react'
import { CircularProgress } from '@material-ui/core'
import { Toggle } from 'src/components/app-ui'
import URL from 'src/api/url/api'
import { useCourseSettings } from 'src/hooks/useCourseSettings'
import { initialThemeSettings } from 'src/model/theme'
import { mediaImageUploadAction } from 'src/reducers/media-image/action'
import FontSelect from 'src/pages/workspace/branding/font-select'
import { useWorkspaceBranding } from 'src/utils/workspace-logo'
import ColorField from './color-field'
import {
  ALIGNMENTS,
  FONT_SIZES,
  LAYOUTS,
  PRESETS,
  PREVIEW_SIZES,
  STRETCH_DEFAULT,
  STRETCH_FULL,
  cssColor,
  modifiedCount,
  presetOf,
  toHex,
  withBrandingTheme,
} from './theme-model'
import useStyles from './styles'

const SETTING_KEY = 'theme'
// The upload saga skips the callback on failure (it shows its own error).
const UPLOAD_TIMEOUT = 30000
const MAX_IMAGE_BYTES = 5 * 1024 * 1024

const backgroundOf = theme =>
  theme.bodyBackgroundImage
    ? `center / cover no-repeat url("${theme.bodyBackgroundImage}"), ${cssColor(
        theme.backgroundColor,
        '#FFFFFF',
      )}`
    : cssColor(theme.backgroundColor, '#FFFFFF')

const fileNameOf = url => {
  try {
    return decodeURIComponent(url.split('?')[0].split('/').pop())
  } catch (error) {
    return url
  }
}

/** A preset card: its background with a sample page in its text color. */
const PresetCard = ({ preset, selected, onPick }) => {
  const classes = useStyles()
  return (
    <button
      type="button"
      title={preset.label}
      className={`${classes.preset} ${selected ? classes.presetOn : ''}`}
      onClick={onPick}
    >
      <div
        className={classes.presetThumb}
        style={{ background: backgroundOf(preset.theme) }}
      >
        <div className={classes.presetCard}>
          <span
            className={classes.presetLine}
            style={{ background: cssColor(preset.theme.fontColor, '#002E47') }}
          />
          <span className={classes.presetLine2} />
        </div>
      </div>
      <span className={classes.presetLabel}>{preset.label}</span>
    </button>
  )
}

/** The course page with the theme applied, in the chosen layout. */
const Preview = ({ theme, courseName, pages }) => {
  const classes = useStyles()
  const layout = LAYOUTS.find(item => item.value === theme.layout) || LAYOUTS[0]
  const [heading, text] = PREVIEW_SIZES[theme.fontSize] || PREVIEW_SIZES.M
  const fontColor = cssColor(theme.fontColor, '#002E47')
  const buttonColor = cssColor(theme.buttonColor, '#015FFB')
  const pageBackground = cssColor(theme.cardBackgroundColor, '#FFFFFF')
  const dark = /^#(0|1|2|3)/.test(toHex(theme.cardBackgroundColor) || '')
  const font = `'${theme.font || 'Inter'}', Inter, sans-serif`
  const previewPages = pages.length ? pages : ['Untitled page']

  return (
    <div
      className={classes.previewFrame}
      style={{ background: backgroundOf(theme), fontFamily: font }}
    >
      {layout.rail && (
        <div className={classes.rail} style={{ color: fontColor }}>
          <div className={classes.railTitle}>{courseName}</div>
          {previewPages.map((page, index) => (
            <div
              key={`${page}-${index}`}
              className={classes.railItem}
              style={{
                background: index === 0 ? 'rgba(0,0,0,.06)' : 'transparent',
                fontWeight: index === 0 ? 600 : 400,
              }}
            >
              {page}
            </div>
          ))}
        </div>
      )}
      <div className={classes.stage}>
        {layout.top && (
          <div className={classes.dots}>
            {previewPages.map((page, index) => (
              <div
                key={`${page}-${index}`}
                className={classes.dot}
                style={{
                  background: index === 0 ? buttonColor : 'rgba(0,0,0,.12)',
                }}
              />
            ))}
          </div>
        )}
        <div
          className={classes.stageBody}
          style={{
            alignItems:
              theme.cardAlignment === 'center' ? 'center' : 'flex-start',
          }}
        >
          {layout.value === 1 && (
            <div
              className={classes.arrow}
              style={{ background: '#FFFFFF', color: '#002E47' }}
            >
              <Icon icon="tabler:chevron-left" width={14} height={14} />
            </div>
          )}
          <div
            className={classes.page}
            style={{
              maxWidth: theme.stretch === STRETCH_FULL ? '100%' : 360,
              background: pageBackground,
              color: fontColor,
            }}
          >
            <div className={classes.pageTitle} style={{ fontSize: heading }}>
              What is stress? The foundation
            </div>
            <div className={classes.pageText} style={{ fontSize: text }}>
              Stress is the body's response to any demand. A little of it
              sharpens focus; too much of it, for too long, wears you down.
            </div>
            <div
              className={classes.pageMedia}
              style={{
                background: dark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.05)',
              }}
            />
            {(layout.value === 0 || layout.value === 2) && (
              <div
                className={classes.continue}
                style={{ background: buttonColor, fontSize: text }}
              >
                Continue
                <Icon icon="tabler:arrow-right" width={14} height={14} />
              </div>
            )}
          </div>
          {layout.value === 1 && (
            <div
              className={classes.arrow}
              style={{ background: buttonColor, color: '#FFFFFF' }}
            >
              <Icon icon="tabler:chevron-right" width={14} height={14} />
            </div>
          )}
        </div>
        {layout.bar && (
          <div className={classes.bar} style={{ color: fontColor }}>
            <span>Page 1 of {previewPages.length}</span>
            <div className={classes.barButtons}>
              <div
                className={classes.barButton}
                style={{
                  background: '#FFFFFF',
                  border: '1px solid rgba(0,0,0,.1)',
                  color: '#002E47',
                }}
              >
                <Icon icon="tabler:chevron-left" width={12} height={12} />
              </div>
              <div
                className={classes.barButton}
                style={{ background: buttonColor, color: '#FFFFFF' }}
              >
                <Icon icon="tabler:chevron-right" width={12} height={12} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/** A miniature of a page layout for the layout picker. */
const LayoutThumb = ({ layout }) => {
  const classes = useStyles()
  return (
    <>
      {layout.rail && (
        <div className={classes.layoutRail}>
          <div className={classes.layoutRailLine} />
          <div className={classes.layoutRailLine} style={{ width: '70%' }} />
          <div className={classes.layoutRailLine} />
        </div>
      )}
      <div className={classes.layoutMain}>
        {layout.top && (
          <div className={classes.layoutTop}>
            <div className={classes.layoutTopLine} />
            <div className={classes.layoutTopLine} />
            <div className={classes.layoutTopLine} />
          </div>
        )}
        <div
          className={classes.layoutPage}
          style={{ width: layout.cardWidth }}
        />
        {layout.arrow && (
          <div className={classes.layoutFoot} style={{ width: '40%' }} />
        )}
        {layout.bar && (
          <div className={classes.layoutFoot} style={{ width: '100%' }} />
        )}
      </div>
    </>
  )
}

const Segmented = ({ options, value, onChange }) => {
  const classes = useStyles()
  return (
    <div className={classes.segmented}>
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          className={`${classes.segment} ${
            option.value === value ? classes.segmentOn : ''
          }`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

const BackgroundImageField = ({ value, onChange }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [uploading, setUploading] = useState(false)
  const timeout = useRef()

  useEffect(() => () => clearTimeout(timeout.current), [])

  const handleFile = event => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      enqueueSnackbar('The image must be PNG or JPG.', { variant: 'error' })
      return
    }
    if (file.size > MAX_IMAGE_BYTES) {
      enqueueSnackbar('The image must be 5 MB or smaller.', {
        variant: 'error',
      })
      return
    }
    setUploading(true)
    timeout.current = setTimeout(() => setUploading(false), UPLOAD_TIMEOUT)
    dispatch(
      mediaImageUploadAction(file, '', result => {
        clearTimeout(timeout.current)
        setUploading(false)
        if (result?.url) onChange(URL.STORAGE + result.url)
      }),
    )
  }

  return (
    <div className={classes.field}>
      <span className={classes.label}>Background image</span>
      {value ? (
        <div className={classes.imageRow}>
          <div
            className={classes.imageThumb}
            style={{ backgroundImage: `url("${value}")` }}
          />
          <span className={classes.imageName}>{fileNameOf(value)}</span>
          <button
            type="button"
            className={classes.imageRemove}
            onClick={() => onChange('')}
          >
            Remove
          </button>
        </div>
      ) : (
        <label className={classes.upload}>
          {uploading ? (
            <CircularProgress size={22} />
          ) : (
            <>
              <Icon
                icon="tabler:photo"
                width={20}
                height={20}
                color="#919EAB"
              />
              <span className={classes.uploadHint}>PNG or JPG, up to 5 MB</span>
              <span className={classes.uploadButton}>
                <Icon icon="tabler:upload" width={14} height={14} />
                Upload image
              </span>
            </>
          )}
          <input
            type="file"
            accept="image/png,image/jpeg"
            hidden
            disabled={uploading}
            onChange={handleFile}
          />
        </label>
      )}
    </div>
  )
}

/**
 * Course > Theme: presets, a preview of the course page and the settings.
 * Changes show in the preview right away and are saved with Apply theme.
 */
const ThemeTab = () => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const course = useSelector(state => state.course.course)
  const sections = useSelector(state => state.course.courseCards)
  const { execute, update, exists, value } = useCourseSettings(SETTING_KEY)
  const [theme, setTheme] = useState(
    exists && value ? value : initialThemeSettings,
  )
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (exists && value) setTheme(value)
  }, [exists, value])

  const set = patch => setTheme(current => ({ ...current, ...patch }))
  // Shown with the branding's button color and font where the theme keeps the
  // defaults; `theme` (saved) is unchanged.
  const branding = useWorkspaceBranding()
  const shown = useMemo(() => withBrandingTheme(theme, branding), [
    theme,
    branding,
  ])

  const preset = presetOf(theme)
  const base = (preset || PRESETS[0]).theme
  const modified = modifiedCount(theme)
  const layout = LAYOUTS.find(item => item.value === theme.layout) || LAYOUTS[0]

  const pages = useMemo(
    () =>
      (sections || [])
        .flatMap(section => section.cards || [])
        .map(card => card.title || 'Untitled page')
        .slice(0, 4),
    [sections],
  )

  const save = () => {
    setSaving(true)
    const done = () => {
      setSaving(false)
      enqueueSnackbar('Saved', { variant: 'success' })
    }
    if (exists) update(theme, done)
    else execute(theme, done)
    // The save sagas skip the callback on failure (they show their own error).
    setTimeout(() => setSaving(false), 15000)
  }

  return (
    <div className={classes.root}>
      <div className={classes.presetsCard}>
        <div className={classes.presetsHead}>
          <div className={classes.heading}>
            <span className={classes.title}>Theme</span>
            <span className={classes.desc}>
              Pick a preset, then fine-tune it. The landing page has its own
              theme.
            </span>
          </div>
          {modified > 0 && (
            <div className={classes.modified}>
              <span className={classes.modifiedBadge}>
                {preset.label} · {modified} modified
              </span>
              <button
                type="button"
                className={classes.reset}
                onClick={() => setTheme(preset.theme)}
              >
                Reset
              </button>
            </div>
          )}
        </div>
        <div className={classes.presets}>
          {PRESETS.map(item => (
            <PresetCard
              key={item.name}
              preset={item}
              selected={preset?.name === item.name}
              onPick={() => setTheme(item.theme)}
            />
          ))}
        </div>
      </div>

      <div className={classes.body}>
        <div className={classes.previewCard}>
          <div className={classes.previewHead}>
            <span className={classes.title}>Course page preview</span>
            <span className={classes.previewMeta}>
              {preset ? `${preset.label} · ` : ''}
              {layout.label}
            </span>
          </div>
          <div className={classes.previewStage}>
            <Preview theme={shown} courseName={course?.name} pages={pages} />
          </div>
        </div>

        <div className={classes.settingsWrap}>
          <div className={classes.settings}>
            <div className={classes.settingsScroll}>
              <div className={classes.field}>
                <span className={classes.label}>Typography</span>
                <FontSelect
                  value={shown.font || 'Inter'}
                  onChange={font => set({ font })}
                />
              </div>

              <div className={classes.field}>
                <span className={classes.label}>Size</span>
                <Segmented
                  options={FONT_SIZES.map(size => ({
                    value: size,
                    label: size,
                  }))}
                  value={theme.fontSize || 'M'}
                  onChange={fontSize => set({ fontSize })}
                />
              </div>

              <ColorField
                label="Text color"
                value={theme.fontColor}
                suggestions={[
                  toHex(base.fontColor) || '#002E47',
                  '#002E47',
                  '#1F1F1F',
                  '#FFFFFF',
                ]}
                onChange={fontColor => set({ fontColor })}
              />
              <ColorField
                label="Button color"
                value={shown.buttonColor}
                suggestions={[
                  toHex(base.buttonColor) || '#015FFB',
                  '#015FFB',
                  '#ED4245',
                  '#29D25D',
                ]}
                onChange={buttonColor => set({ buttonColor })}
              />

              <div className={classes.field}>
                <span className={classes.label}>
                  Page layout · {layout.label}
                </span>
                <div className={classes.layouts}>
                  {LAYOUTS.map(item => (
                    <button
                      key={item.value}
                      type="button"
                      title={item.label}
                      className={`${classes.layout} ${
                        item.value === layout.value ? classes.layoutOn : ''
                      }`}
                      onClick={() => set({ layout: item.value })}
                    >
                      <LayoutThumb layout={item} />
                    </button>
                  ))}
                </div>
              </div>

              <ColorField
                label="Background color"
                value={theme.backgroundColor}
                suggestions={[
                  toHex(base.backgroundColor) || '#FFFFFF',
                  '#FFFFFF',
                  '#F4F6F8',
                  '#002E47',
                ]}
                onChange={backgroundColor => set({ backgroundColor })}
              />
              <ColorField
                label="Page background color"
                value={theme.cardBackgroundColor}
                suggestions={[
                  toHex(base.cardBackgroundColor) || '#FFFFFF',
                  '#F7F7F7',
                  '#FFF8EC',
                  '#1F1F1F',
                ]}
                onChange={cardBackgroundColor => set({ cardBackgroundColor })}
              />

              <BackgroundImageField
                value={theme.bodyBackgroundImage}
                onChange={bodyBackgroundImage => set({ bodyBackgroundImage })}
              />

              <div className={classes.field}>
                <span className={classes.label}>Page vertical alignment</span>
                <Segmented
                  options={ALIGNMENTS}
                  value={theme.cardAlignment === 'center' ? 'center' : 'top'}
                  onChange={cardAlignment => set({ cardAlignment })}
                />
              </div>

              <div className={classes.stretchRow}>
                <div>
                  <div className={classes.stretchTitle}>Page stretch</div>
                  <div className={classes.stretchDesc}>
                    Page fills the full width.
                  </div>
                </div>
                <Toggle
                  checked={theme.stretch === STRETCH_FULL}
                  onChange={full =>
                    set({ stretch: full ? STRETCH_FULL : STRETCH_DEFAULT })
                  }
                />
              </div>
            </div>
            <div className={classes.settingsFoot}>
              <button
                type="button"
                className={classes.apply}
                disabled={saving}
                onClick={save}
              >
                {saving ? 'Saving…' : 'Apply theme'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ThemeTab
